<template>
  <v-row justify="center" align="center" class="fill-height">
    <v-col cols="12" sm="10" md="6" lg="4">
      <v-card>
        <v-card-title>{{ $t("user.change_password") }}</v-card-title>
        <v-card-subtitle class="pt-2">
          You must change your password before continuing.
        </v-card-subtitle>

        <v-card-text>
          <v-alert v-if="errorMessage" type="error" outlined>
            {{ errorMessage }}
          </v-alert>

          <v-form ref="changePasswordForm" v-model="formValid">
            <v-text-field
              v-model="password"
              :append-icon="showCurrent ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showCurrent ? 'text' : 'password'"
              :label="$t('user.password')"
              :rules="[required]"
              outlined
              @click:append="showCurrent = !showCurrent"
            />

            <v-text-field
              v-model="passwordNew"
              :append-icon="showNew ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showNew ? 'text' : 'password'"
              :label="$t('user.password_new')"
              :rules="[required, passwordPolicyRule]"
              outlined
              @click:append="showNew = !showNew"
            />

            <PasswordRequirements
              v-if="administrativeRole"
              :value="passwordNew"
            />

            <v-text-field
              v-model="passwordConfirm"
              :append-icon="showConfirm ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showConfirm ? 'text' : 'password'"
              :label="$t('user.password_confirm')"
              :rules="[required, confirmRule]"
              outlined
              @click:append="showConfirm = !showConfirm"
              @keyup.enter="changePassword"
            />
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn text :disabled="submitting" @click.stop="handleLogout">
            {{ $t("logout") }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!canSubmit"
            @click="changePassword"
          >
            {{ $t("apply") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import Axios from "axios";
import config from "@/config";
import PasswordRequirements from "@/components/PasswordRequirements";
import { clearAuthSession } from "@/utils/authSession";
import {
  isAdministrativeRole,
  isStrongPassword,
} from "@/utils/passwordPolicy";

const qs = require("querystring");

export default {
  name: "ChangePassword",
  components: {
    PasswordRequirements,
  },
  data() {
    return {
      password: "",
      passwordNew: "",
      passwordConfirm: "",
      showCurrent: false,
      showNew: false,
      showConfirm: false,
      formValid: false,
      submitting: false,
      errorMessage: "",
    };
  },
  computed: {
    administrativeRole() {
      return isAdministrativeRole(localStorage.getItem("role"));
    },
    passwordIsValid() {
      return this.administrativeRole
        ? isStrongPassword(this.passwordNew)
        : this.passwordNew.length >= 4;
    },
    canSubmit() {
      return (
        this.formValid &&
        this.passwordIsValid &&
        this.passwordNew === this.passwordConfirm &&
        !this.submitting
      );
    },
  },
  methods: {
    required(value) {
      return !!value || this.$i18n.t("user.required");
    },
    passwordPolicyRule(value) {
      if (this.administrativeRole) {
        return (
          isStrongPassword(value) ||
          "The password does not meet the required complexity."
        );
      }

      return (
        String(value || "").length >= 4 ||
        this.$i18n.t("password_length_characters", { passLength: 4 })
      );
    },
    confirmRule(value) {
      return (
        value === this.passwordNew ||
        this.$i18n.t("user.password_confirm_error")
      );
    },
    async changePassword() {
      this.errorMessage = "";
      if (!this.$refs.changePasswordForm.validate() || !this.canSubmit) return;

      this.submitting = true;
      try {
        await Axios.post(
          config.apiBaseUrl + "set-password",
          qs.stringify({
            OldPassword: this.password,
            NewPassword: this.passwordNew,
          }),
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken"),
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );
        this.handleLogout();
      } catch (error) {
        const responseData = error.response && error.response.data;
        this.errorMessage =
          (responseData && responseData.message) ||
          responseData ||
          "Unable to change the password.";
      } finally {
        this.submitting = false;
      }
    },
    handleLogout() {
      clearAuthSession();
      window.location.replace("/");
    },
  },
};
</script>
